import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import { presentFormatedData, presentDate } from '../../../functionalComponents/dataFormatFunctions';

const useStyles = makeStyles((theme) => ({
  font: {
    fontWeight: 600,
    textAlign: 'initial',
    paddingBottom: '0.25rem',
    fontSize: '1rem',
  },
  fontPadding: {
    textAlign: 'initial',
    overflow: 'auto',
    paddingTop: '0.25rem',
    fontSize: '1rem',
  },
  grid: {
    paddingBottom: '2rem',
    paddingTop: '1rem',
    paddingRight: '1.5rem',
  },
  borders: {
    border: '2px solid #707070',
    display: 'flex',
    width: '1.6rem',
    backgroundColor: 'grey',
  },
  title: {
    fontWeight: 500,
    fontSize: '1.5rem',
    textAlign: 'left',
    paddingBottom: '0.3rem',
  },
  rowContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  // Mirrors the GRN Information table styling in QuickView.jsx so the new
  // per-delivery shipping table below reads as the same visual pattern.
  tableContainer: {
    marginTop: theme.spacing(0),
    boxShadow: 'none',
    borderRadius: '0px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#fff',
    maxWidth: '800px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#F5F5F5',
  },
  headerCell: {
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#333',
    padding: '8px 10px',
    borderBottom: '1px solid #E0E0E0',
    whiteSpace: 'nowrap',
  },
  tableRow: {
    backgroundColor: '#fff',
  },
  tableCell: {
    fontSize: '14px',
    color: '#333',
    padding: '6px 10px',
    borderBottom: '1px solid #E0E0E0',
    whiteSpace: 'nowrap',
  },
}));

const PostShipmentInformation = (props) => {
  const classes = useStyles();
  const { batchDetails, selectedBatch } = props;
  

  return (
    <React.Fragment>
      <Grid container>
        <Grid item xs={8} sm={12} md={12} className={classes.grid}>
          <Typography variant='h4' className={classes.title}>
            Post-Shipment Information
          </Typography>
          <span className={classes.borders}></span>
        </Grid>

        <Grid container item xs={12} className={classes.rowContainer}>
          {batchDetails.dispatchDate && (
            <Grid item xs={12} sm={4} md={4} lg={3} className={classes.grid}>
              <Typography variant='body2' className={classes.font}>
                Shipment Date
              </Typography>
              <Typography variant='body2' className={classes.fontPadding}>
                {presentDate(batchDetails.dispatchDate)}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Grid container item xs={12} className={classes.rowContainer}>
          {batchDetails.sealNumber && (
            <Grid item xs={12} sm={4} md={4} lg={3} className={classes.grid}>
              <Typography variant='body2' className={classes.font}>
                Seal Number
              </Typography>
              <Typography variant='body2' className={classes.fontPadding}>
                {presentFormatedData(batchDetails.sealNumber)}
              </Typography>
            </Grid>
          )}
          {batchDetails.shipperName && (
            <Grid item xs={12} sm={4} md={4} lg={3} className={classes.grid}>
              <Typography variant='body2' className={classes.font}>
                Shipper Name
              </Typography>
              <Typography variant='body2' className={classes.fontPadding}>
                {presentFormatedData(batchDetails.shipperName)}
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Produced Qty / UOM, sourced from the same fetchDeliveryScheduleBatchDetails response. */}
        <Grid container item xs={12} className={classes.rowContainer}>
          {batchDetails?.producedQuantity !== null && batchDetails?.producedQuantity !== undefined && (
            <Grid item xs={12} sm={4} md={4} lg={3} className={classes.grid}>
              <Typography variant='body2' className={classes.font}>
                Produced Qty
              </Typography>
              <Typography variant='body2' className={classes.fontPadding}>
                {presentFormatedData(batchDetails.producedQuantity)}
              </Typography>
            </Grid>
          )}
          {batchDetails?.batchUOM && (
            <Grid item xs={12} sm={4} md={4} lg={3} className={classes.grid}>
              <Typography variant='body2' className={classes.font}>
                UOM
              </Typography>
              <Typography variant='body2' className={classes.fontPadding}>
                {presentFormatedData(batchDetails.batchUOM)}
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Total Dispatch Qty / Dispatch UOM / Remaining Qty, plus the per-delivery
            breakdown table below — both derived from postShipmentShippingData, the
            per-shipment array in the same API response (one entry per delivery). */}
        {Array.isArray(batchDetails?.postShipmentShippingData) && batchDetails.postShipmentShippingData.length > 0 && (
          <React.Fragment>
            <Grid container item xs={12} className={classes.rowContainer}>
              <Grid item xs={12} sm={4} md={4} lg={3} className={classes.grid}>
                <Typography variant='body2' className={classes.font}>
                  Total Dispatch Qty
                </Typography>
                <Typography variant='body2' className={classes.fontPadding}>
                  {presentFormatedData(
                    batchDetails.postShipmentShippingData.reduce(
                      (sum, shipment) => sum + (Number(shipment?.dispatchQuantity) || 0),
                      0
                    )
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={3} className={classes.grid}>
                <Typography variant='body2' className={classes.font}>
                  Dispatch UOM
                </Typography>
                <Typography variant='body2' className={classes.fontPadding}>
                  {presentFormatedData(
                    batchDetails.postShipmentShippingData[0]?.dispatchQuantityUOM || batchDetails?.batchUOM
                  )}
                </Typography>
              </Grid>
              {batchDetails?.remainingQuantityToBeDispatched !== null && batchDetails?.remainingQuantityToBeDispatched !== undefined && (
                <Grid item xs={12} sm={4} md={4} lg={3} className={classes.grid}>
                  <Typography variant='body2' className={classes.font}>
                    Remaining Qty
                  </Typography>
                  <Typography variant='body2' className={classes.fontPadding}>
                    {presentFormatedData(
                      (() => {
                        // Round to 3 decimals for display — the raw value can carry
                        // floating-point noise (e.g. 1.716613766689079e-7 instead of 0)
                        // from backend subtraction, which renders in ugly exponential
                        // notation if passed straight through.
                        const num = Number(batchDetails.remainingQuantityToBeDispatched);
                        if (!Number.isFinite(num)) return batchDetails.remainingQuantityToBeDispatched;
                        const rounded = Number(num.toFixed(3));
                        // A genuinely-zero (or noise-rounded-to-zero) remainder should just
                        // read "0" — showing "0.000" reads as if there's real precision behind
                        // three decimals when there isn't, which is confusing for the user.
                        return rounded === 0 ? '0' : rounded.toFixed(3);
                      })()
                    )}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Grid item xs={12} lg={11} style={{ paddingBottom: '1rem', paddingTop: '0.5rem' }}>
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell className={classes.headerCell}>Delivery Number</TableCell>
                      <TableCell className={classes.headerCell} style={{ textAlign: 'right' }}>Dispatch Qty</TableCell>
                      <TableCell className={classes.headerCell}>Dispatch UOM</TableCell>
                      <TableCell className={classes.headerCell}>Planned Goods Issue Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchDetails.postShipmentShippingData.map((shipment, index) => (
                      <TableRow key={shipment?.deliveryNumber || index} className={classes.tableRow}>
                        <TableCell className={classes.tableCell}>
                          {presentFormatedData(shipment?.deliveryNumber, 'N/A')}
                        </TableCell>
                        <TableCell className={classes.tableCell} style={{ textAlign: 'right' }}>
                          {presentFormatedData(shipment?.dispatchQuantity, 'N/A')}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {presentFormatedData(shipment?.dispatchQuantityUOM, 'N/A')}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {presentDate(shipment?.plannedGoodsIssueDate, 'N/A')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      <hr />
    </React.Fragment>
  );
};

export default PostShipmentInformation;